import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CommonService } from '@fitness-force/api';
import { OwnershipLevel, Status } from '@fitness-force/enum';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type { CustomAutomationData, Franchise, TENANTSINTERFACE, TENANT_MODEL } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from '@fitness-force/state';
import { ConfirmEventType, ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { StyleClassModule } from 'primeng/styleclass';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'fitness-force-custom-automation-popup',
  templateUrl: './custom-automation-popup.component.html',
  styleUrls: ['./custom-automation-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    SelectModule,
    MultiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    StyleClassModule,
    InputTextModule,
    TextareaModule,
    ErrorMsgComponent,
    TranslateModule,
    NgClass
]
})
export class CustomAutomationPopupComponent implements OnInit {
  private commonService = inject(CommonService);
  private ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private fb = inject(UntypedFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private store = inject(Store);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);


  franchises: any[] = [];
  selectedFranchise: {name: string} | undefined;

  modelData:CustomAutomationData ={} as CustomAutomationData;
  createAutomationForm: UntypedFormGroup = new UntypedFormGroup({});
  accessedtenants: TENANT_MODEL[] = [];
  franchiseTenants: TENANT_MODEL[] = [];
  tenantId:number=0;
  logginTenant:TENANT_MODEL= {} as TENANT_MODEL;
  btnLoading:boolean=false;
  franchiseList:Franchise[]= [];
  private preselectedTenantsApplied = false;


  constructor() { 

    if (this.config.data) {
      this.modelData = this.config.data;
    }
  }

  ngOnInit(): void {
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      this.logginTenant= tenant.selected_tenant;
      this.accessedtenants = tenant.accessed_tenant ? tenant.accessed_tenant.filter(t => t.status == Status.Active.toString() || t.status == Status[Status.Active]) : [];
      this.franchiseList = this.getDistinctFranchises(tenant.accessed_tenant ? tenant.accessed_tenant.filter(t => t.status == Status.Active.toString() || t.status == Status[Status.Active]) : []);
      this.setPreselectedTenantsForUpdateMode();
    });

    this.createAutomationForm = this.fb.group({
      automation_name: ['', [Validators.required]],
      automation_description: ['', [Validators.required]],
      ownership_type: ['', [Validators.required]],
      selected_frenchise: [''],
      selected_frenchise_tenants: [''],
      selected_company_tenants: [''],
    });

    if(this.modelData && this.modelData.formGroup && (Object.keys(this.modelData.formGroup.controls).length > 0 || this.modelData.event =='update')){
      if(Object.keys(this.modelData.formGroup.controls).length > 0)
      {
        this.createAutomationForm = this.modelData.formGroup;
      }
      else{
        this.AutomationNameFormControl.setValue(this.modelData.name);
        this.AutomationDescriptionFormControl.setValue(this.modelData.description);
        // Normalize ownershiplevel: API may return the string key (e.g. "Tenant") while the
        // template and setPreselectedTenantsForUpdateMode compare against the numeric enum value.
        this.modelData.ownershiplevel = this.normalizeOwnershipLevel(this.modelData.ownershiplevel);
        this.OwnershipFormControl.setValue(this.modelData.ownershiplevel);
      }
      this.createAutomationForm.updateValueAndValidity();
      this.cd.detectChanges();
    }
    else{
      this.OwnershipFormControl.setValue(OwnershipLevel.Tenant);
    }

    if(this.franchiseList && this.franchiseList.length == 1)
    {
      this.SelectedFrenchiseFormControl.setValue(this.franchiseList[0]);
      this.franchiseTenants = this.accessedtenants.filter(t => t.franchise_group_id == this.franchiseList[0].franchise_group_id)!;
    }

    this.SelectedFrenchiseFormControl.valueChanges
    .pipe(distinctUntilChanged())
    .subscribe((value: Franchise) => {
      if(value)
         this.franchiseTenants = this.accessedtenants.filter(t => t.franchise_group_id == value.franchise_group_id)!;
    });

    this.setPreselectedTenantsForUpdateMode();
  }

  getDistinctFranchises(tenants:TENANT_MODEL[]) {
    const distinctFranchises: { [key: number]: Franchise } = {};
    tenants.forEach(tenant => {
      if(tenant.franchise_group_id > 0)
      {
        const groupId = tenant.franchise_group_id;
        if (!distinctFranchises[groupId]) {
          const frenchise:Franchise ={
            franchise_group_id: tenant.franchise_group_id,
            franchise_group_primary_name: tenant.franchise_group_primary_name,
            franchise_group_secondary_name: tenant.franchise_group_secondary_name
          }
            distinctFranchises[groupId] = frenchise;
        }
      }
    });
    return Object.values(distinctFranchises);
}

  private setPreselectedTenantsForUpdateMode(): void {
    if (
      this.preselectedTenantsApplied ||
      this.modelData?.event !== 'update' ||
      !this.createAutomationForm?.controls['selected_frenchise_tenants']
    ) {
      return;
    }
    if (this.modelData.ownershiplevel === OwnershipLevel.Franchise) {
      // Resolve the franchise: first try by franchiseId, then fall back to the already-selected
      // franchise (auto-set when there is only one) or the single franchise in the list.
      // franchise_id may be 0 in the API response even for Franchise-level automations.
      let franchise: Franchise | undefined = this.modelData.franchiseId > 0
        ? this.franchiseList.find(f => f.franchise_group_id === this.modelData.franchiseId)
        : undefined;

      if (!franchise) {
        franchise = this.SelectedFrenchiseFormControl.value ??
          (this.franchiseList?.length === 1 ? this.franchiseList[0] : undefined);
      }

      if (franchise) {
        this.SelectedFrenchiseFormControl.setValue(franchise);
        this.franchiseTenants = this.accessedtenants.filter(
          (t) => t.franchise_group_id === franchise!.franchise_group_id
        );
        const selectedIds = this.modelData.selectedFrenchiseTenants ?? [];
        const selectedTenantObjects = this.franchiseTenants.filter((t) =>
          selectedIds.includes(t.id)
        );
        this.SelectedFranchiseTenantsFormControl.setValue(
          selectedTenantObjects.length > 0 ? selectedTenantObjects : null
        );
      }
    }
    if (
      this.modelData.ownershiplevel === OwnershipLevel.Company &&
      this.modelData.selectedCompanyTenants?.length > 0 &&
      this.accessedtenants?.length > 0
    ) {
      const selectedIds = this.modelData.selectedCompanyTenants;
      const selectedTenantObjects = this.accessedtenants.filter((t) =>
        selectedIds.includes(t.id)
      );
      this.SelectedCompanyTenantsFormControl.setValue(
        selectedTenantObjects.length > 0 ? selectedTenantObjects : null
      );
    }
    this.preselectedTenantsApplied = true;
    this.cd.detectChanges();
  }

  SelectOwnership(ownershipType:OwnershipLevel)
  {
    if(this.modelData.ownershiplevel && this.modelData.ownershiplevel != ownershipType && this.modelData.actionsConfigured)
    {
      this.confirmDialog(ownershipType);
    }
    else{
      switch (ownershipType) {
        case OwnershipLevel.Company:
            this.OwnershipFormControl.setValue(ownershipType);
            this.SelectedFranchiseTenantsFormControl.clearValidators();
            this.SelectedFranchiseTenantsFormControl.setValue(null);
            this.SelectedCompanyTenantsFormControl.setValidators(Validators.required);
            this.SelectedFrenchiseFormControl.clearValidators();
            this.SelectedFrenchiseFormControl.setValue(null);
          break;
          case OwnershipLevel.Franchise:
            this.OwnershipFormControl.setValue(ownershipType);
            this.SelectedFranchiseTenantsFormControl.setValidators(Validators.required);
            this.SelectedCompanyTenantsFormControl.clearValidators();
            this.SelectedCompanyTenantsFormControl.setValue(null);
            this.SelectedFrenchiseFormControl.setValidators(Validators.required);
            if(this.franchiseList && this.franchiseList.length == 1)
            {
              this.SelectedFrenchiseFormControl.setValue(this.franchiseList[0]);
              this.franchiseTenants = this.accessedtenants.filter(t => t.franchise_group_id == this.franchiseList[0].franchise_group_id)!;
            }
          break;
          case OwnershipLevel.Tenant:
            this.OwnershipFormControl.setValue(ownershipType);
            this.SelectedFranchiseTenantsFormControl.clearValidators();
            this.SelectedFranchiseTenantsFormControl.setValue(null);
            this.SelectedCompanyTenantsFormControl.clearValidators();
            this.SelectedCompanyTenantsFormControl.setValue(null);
            this.SelectedFrenchiseFormControl.clearValidators();
            this.SelectedFrenchiseFormControl.setValue(null);
          break;
      }
      this.createAutomationForm.updateValueAndValidity();
      this.cd.detectChanges();
    }  
    
  }

  SaveData()
  {
    if(this.createAutomationForm.valid)
    {
      this.btnLoading = true;
      this.modelData.name = this.AutomationNameFormControl.value;
      this.modelData.description = this.AutomationDescriptionFormControl.value;
      this.modelData.ownershiplevel = this.OwnershipFormControl.value;
      this.modelData.selectedFrenchise = this.SelectedFrenchiseFormControl.value;
      this.modelData.selectedCompanyTenants = this.SelectedCompanyTenantsFormControl.value ? this.SelectedCompanyTenantsFormControl.value.map((o: any) => o.id) : [];
      this.modelData.selectedFrenchiseTenants = this.SelectedFranchiseTenantsFormControl.value ? this.SelectedFranchiseTenantsFormControl.value.map((o: any) => o.id) : [];
      this.modelData.tenantId = this.OwnershipFormControl.value == OwnershipLevel.Tenant ? this.tenantId :  this.tenantId;
      this.modelData.formGroup= this.createAutomationForm;
      this.ref.close(this.modelData);
    }
    else {
      Object.keys(this.createAutomationForm.controls).forEach((field) => {
        const control = this.createAutomationForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      const firstElementWithError: Element | null = document.querySelector('.ng-invalid');
      if (firstElementWithError) firstElementWithError.scrollIntoView({ behavior: 'smooth' });
      this.btnLoading=false;
      this.cd.detectChanges();
    }
  }

  confirmDialog(ownershipType:OwnershipLevel) {
    this.translateService.get('AUTOMATION.CREATE.CONIFRM_INFO').subscribe((translations) => {
      this.confirmationService.confirm({
        header:"Confirmation",
        message: translations,
        icon: 'fa fa-exclamation-triangle',
        accept: () => {
          this.modelData.dataChanged=true;
          switch (ownershipType) {
            case OwnershipLevel.Company:
                this.OwnershipFormControl.setValue(ownershipType);
                this.SelectedFranchiseTenantsFormControl.clearValidators();
                this.SelectedFranchiseTenantsFormControl.setValue(null);
                this.SelectedCompanyTenantsFormControl.setValidators(Validators.required);
                this.SelectedFrenchiseFormControl.clearValidators();
                this.SelectedFrenchiseFormControl.setValue(null);
              break;
              case OwnershipLevel.Franchise:
                this.OwnershipFormControl.setValue(ownershipType);
                this.SelectedFranchiseTenantsFormControl.setValidators(Validators.required);
                this.SelectedCompanyTenantsFormControl.clearValidators();
                this.SelectedCompanyTenantsFormControl.setValue(null);
                this.SelectedFrenchiseFormControl.setValidators(Validators.required);
                if(this.franchiseList && this.franchiseList.length == 1)
                {
                  this.SelectedFrenchiseFormControl.setValue(this.franchiseList[0]);
                  this.franchiseTenants = this.accessedtenants.filter(t => t.franchise_group_id == this.franchiseList[0].franchise_group_id)!;
                }
              break;
              case OwnershipLevel.Tenant:
                this.OwnershipFormControl.setValue(ownershipType);
                this.SelectedFranchiseTenantsFormControl.clearValidators();
                this.SelectedFranchiseTenantsFormControl.setValue(null);
                this.SelectedCompanyTenantsFormControl.clearValidators();
                this.SelectedCompanyTenantsFormControl.setValue(null);
                this.SelectedFrenchiseFormControl.clearValidators();
                this.SelectedFrenchiseFormControl.setValue(null);
              break;
          }
          this.createAutomationForm.updateValueAndValidity();
          this.cd.detectChanges();
        },
        reject: (type: any) => {
          switch (type) {
            case ConfirmEventType.REJECT:
              this.modelData.dataChanged=false;
              break;
            case ConfirmEventType.CANCEL:
              this.modelData.dataChanged=false;
              break;
          }
        }
      });
    });
  }
  
  closePopup()
  {
    this.ref.close(null);
  }

  get AutomationNameFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls['automation_name'] as UntypedFormControl;
  }

  get AutomationDescriptionFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls['automation_description'] as UntypedFormControl;
  }

  get OwnershipFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls['ownership_type'] as UntypedFormControl;
  }

  get SelectedFranchiseTenantsFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls['selected_frenchise_tenants'] as UntypedFormControl;
  }

  get SelectedCompanyTenantsFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls['selected_company_tenants'] as UntypedFormControl;
  }

  get SelectedFrenchiseFormControl(): UntypedFormControl {
    return this.createAutomationForm.controls['selected_frenchise'] as UntypedFormControl;
  }

  /** Converts a string key ("Tenant", "Franchise", "Company") to its numeric OwnershipLevel enum value.
   *  This is needed because the API may return the string representation, while the template
   *  and setPreselectedTenantsForUpdateMode both compare against the numeric enum. */
  private normalizeOwnershipLevel(level: any): OwnershipLevel {
    if (typeof level === 'string') {
      const enumValue = OwnershipLevel[level as keyof typeof OwnershipLevel];
      return enumValue !== undefined ? enumValue : OwnershipLevel.Tenant;
    }
    return level ?? OwnershipLevel.Tenant;
  }

  public get ownershipLevel()
  {
    return OwnershipLevel;
  }

}
